import React, { useState, useEffect } from 'react'
import type { RouteChildrenProps } from 'react-router-dom'
import { flowRight as compose } from 'lodash'

import {
  TicketSelectBranch,
  BREADCRUMBS_SELECT_BRANCH_LIST,
} from 'features/Ticket'
import { BranchService } from 'features/Branch'
import { formatTicketBranchList } from 'features/Branch/format'

import {
  useTicketSelectBranchContext,
  withTicketSelectBranches,
} from 'features/Ticket/TicketSelectBranch'
import { LIMIT_PAGE } from 'helpers/constants/common'
import { withBackOfficeCms } from 'helpers/hocs'
import { useBreadcrumbs, useSearchParams, useCancelToken } from 'helpers/hooks'

import type { WithIParam } from 'libs/apis'
import type { ITicketSelectBranchSinglePartial } from 'features/Ticket/TicketSelectBranch'

const TicketSelectBranchContainer: React.FC<RouteChildrenProps> = ({
  location,
}: RouteChildrenProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalPage, setTotalPages] = useState<number>(0)
  const [isError, setIsError] = useState<boolean>(false)

  const { branches, updateTicketSelectBranches } =
    useTicketSelectBranchContext()
  const [searchParams, setSearchParams] = useSearchParams<
    WithIParam<ITicketSelectBranchSinglePartial>
  >({
    _limit: 30,
    _page: 1,
    isActive: true,
  })
  const { newCancelToken, isCancel } = useCancelToken()

  // Add Breadcrumbs
  useBreadcrumbs(BREADCRUMBS_SELECT_BRANCH_LIST)

  const base = 'con-ticket-select-branch'

  const _page = Number(searchParams._page) || 1

  useEffect(() => {
    let isMounted = true

    const getBranches = async () => {
      setIsLoading(true)
      setIsError(false)

      try {
        const response = await BranchService.findAll(searchParams, {
          cancelToken: newCancelToken(),
        })
        const { body, totals } = response

        const formattedBranch = formatTicketBranchList(body)

        setTotalPages(Math.ceil(Number(totals) / LIMIT_PAGE) || 1)
        updateTicketSelectBranches(formattedBranch, _page, searchParams)
        setIsLoading(false)
      } catch (e) {
        if (isCancel(e)) return

        setIsLoading(false)
        setIsError(true)
      }
    }

    if (isMounted) {
      getBranches()
    }

    return () => {
      isMounted = false
    }
  }, [location])

  return (
    <div className={base}>
      <TicketSelectBranch
        branches={branches}
        isLoading={isLoading}
        isError={isError}
        setSearchParams={setSearchParams}
        searchParams={searchParams}
        totalPage={totalPage}
        currentPage={_page}
      />
    </div>
  )
}

export default compose(
  withBackOfficeCms,
  withTicketSelectBranches,
)(TicketSelectBranchContainer)
